import React, { useRef, useEffect } from "react";
import { PlusIcon } from "@heroicons/react/24/solid";
import { Link } from "react-router-dom";

interface ResultsCardProps {
  onCreateLien: () => void;
  reference?: string;
  loading?: boolean;
  disabled?: boolean;
  foundDeed?: boolean;
  documentId?: number;
}

const ResultsCard: React.FC<ResultsCardProps> = ({ onCreateLien, reference, loading = false, disabled = false, foundDeed = false, documentId }) => {
  const notFoundRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    if (!foundDeed && notFoundRef.current) {
      notFoundRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [foundDeed]);

  return (
    <div
      className="bg-[#F9FAFB] rounded-lg px-8 py-6 flex flex-col items-center max-w-md w-full mt-4"
      role="status"
      aria-live="polite"
    >
      {foundDeed && documentId ? (
        <>
          <h3 className="text-[18px] leading-[1.56] tracking-[-0.1%] font-bold text-[#141435] mb-2 text-center font-plus-jakarta">
            Deed Found!
          </h3>
          <p className="text-[16px] leading-[1.75] tracking-[-0.12%] text-[#485D7D] mb-6 text-center font-plus-jakarta">
            View the property info for this Reference # <span className="font-semibold">{reference || "2024-1234D"}</span>
          </p>
          <Link
            to={`/document/${documentId}/property-info`}
            className="w-full bg-[#0B4BB6] text-white font-bold text-[15px] font-plus-jakarta py-2 rounded-full mt-2 hover:bg-[#23235a] transition-colors border-2 border-[#0B4BB6] flex items-center justify-center"
            data-testid="property-info-link"
          >
            Go to Property Info
          </Link>
        </>
      ) : (
        <>
          <h3
            ref={notFoundRef}
            className="text-[18px] leading-[1.56] tracking-[-0.1%] font-bold text-[#141435] mb-2 text-center font-plus-jakarta"
            id="results-heading"
          >
            Deed Was Not Found!
          </h3>

          <p
            className="text-[16px] leading-[1.75] tracking-[-0.12%] text-[#485D7D] mb-6 text-center font-plus-jakarta"
            aria-describedby="results-heading"
          >
            Try searching again or create a property record with this Reference # <span className="font-semibold">{reference || "2024-1234D"}</span>
          </p>

          <button
            type="button"
            className={`flex items-center justify-center gap-2 bg-[#141435] border-2 border-[#141435] text-white font-bold text-[15px] font-plus-jakarta py-2.5 px-6 rounded-full w-full max-w-xs hover:bg-[#23235a] transition-colors ${disabled || loading ? 'opacity-60 cursor-not-allowed' : ''}`}
            onClick={onCreateLien}
            aria-label="Create Frozen Deed"
            disabled={disabled || loading}
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
            ) : (
              <PlusIcon className="w-5 h-5 text-white" />
            )}
            {loading ? 'Creating...' : 'Create Frozen Deed'}
          </button>
        </>
      )}
    </div>
  );
};

export default ResultsCard; 